/** Paise comparison. 40 and 40.00 are the same shelf price. */
export function sameShelfMoney(a, b) {
  const left = Number(a);
  const right = Number(b);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
  return Math.round(left * 100) === Math.round(right * 100);
}

/** Open lots with available qty (backend requires lot_id when any such lot exists). */
export function availableLots(lots) {
  if (!Array.isArray(lots)) return [];
  return lots.filter((lot) => (
    Number(lot.qty) > 0 && String(lot.status || 'OPEN').toUpperCase() !== 'CLOSED'
  ));
}

/**
 * Server `_price_cart` order: selling_price asc, then received_at asc (oldest first).
 * @param {Array<{ sellingPrice?: number, receivedAt?: string|null, lotId?: string }>} lots
 */
export function sortOpenLotsLikeServer(lots) {
  return [...lots].sort((a, b) => {
    const pa = Number(a.sellingPrice);
    const pb = Number(b.sellingPrice);
    if (pa !== pb) return pa - pb;
    const ta = a.receivedAt ? Date.parse(a.receivedAt) : Number.POSITIVE_INFINITY;
    const tb = b.receivedAt ? Date.parse(b.receivedAt) : Number.POSITIVE_INFINITY;
    const taSafe = Number.isFinite(ta) ? ta : Number.POSITIVE_INFINITY;
    const tbSafe = Number.isFinite(tb) ? tb : Number.POSITIVE_INFINITY;
    if (taSafe !== tbSafe) return taSafe - tbSafe;
    return String(a.lotId || '').localeCompare(String(b.lotId || ''));
  });
}

/**
 * Decide how to attach open lots to a cart line (same rules as server `_price_cart`).
 *
 * @param {object} args
 * @param {Array} args.lots - lot rows (`lotId`, `sellingPrice`, `qty`, `mrp`, `receivedAt`)
 * @param {number} args.lineQty
 * @param {boolean} [args.allowShortLots] - WEIGHT or catalog-incomplete: pick oldest even if short
 * @returns {{ action: 'none' } | { action: 'attach', lot: object } | { action: 'pick', lots: object[] }}
 */
export function resolveOpenLotsForLine({ lots, lineQty, allowShortLots = false }) {
  const open = availableLots(lots);
  if (open.length === 0) {
    return { action: 'none' };
  }

  const distinctPrices = [...new Set(open.map((l) => Number(l.sellingPrice)))];
  if (distinctPrices.length > 1) {
    return { action: 'pick', lots: open };
  }

  const sorted = sortOpenLotsLikeServer(open);
  const qty = Number(lineQty);
  const needQty = Number.isFinite(qty) && qty > 0 ? qty : 1;

  let lot;
  if (allowShortLots) {
    lot = sorted[0];
  } else {
    lot = sorted.find((l) => Number(l.qty) >= needQty) || sorted[0];
  }

  return { action: 'attach', lot };
}

/** Oldest open lot at the lowest price. Used when the till cannot ask the cashier to pick. */
export function defaultOpenLot(lots, lineQty = 1, { allowShortLots = false } = {}) {
  const open = availableLots(lots);
  if (!open.length) return null;
  const sorted = sortOpenLotsLikeServer(open);
  if (allowShortLots) return sorted[0];
  const qty = Number(lineQty);
  const needQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
  return sorted.find((lot) => Number(lot.qty) >= needQty) || sorted[0];
}

/**
 * Resolve barcode/lots payload into add-to-cart or pick-lot action.
 * Feature-detect: if `lots` key missing (`hasLots` false) → legacy META-only.
 * When open lots with qty > 0 exist → attach the default lot (lowest price, then oldest).
 *
 * @param {object} args
 * @param {number} [args.lineQty=1] - used when choosing the default open lot
 */
export function resolveLotForCart({ item, lots, hasLots, lineQty = 1 }) {
  if (!hasLots || !Array.isArray(lots)) {
    return {
      action: 'add',
      item,
      lotId: null,
      unitPrice: item.price,
      lotQty: item.stock,
      requiresLot: false,
    };
  }

  const open = availableLots(lots);
  if (open.length === 0) {
    // Lots key present but nothing sellable — legacy-ish META path; server won't require lot_id.
    return {
      action: 'add',
      item,
      lotId: null,
      unitPrice: item.price,
      lotQty: item.stock,
      requiresLot: false,
    };
  }

  const allowShortLots = String(item?.soldBy || '').toUpperCase() === 'WEIGHT'
    || item?.catalogComplete === false;
  const decision = resolveOpenLotsForLine({
    lots: open,
    lineQty,
    allowShortLots,
  });

  const lot = decision.action === 'attach' && decision.lot
    ? decision.lot
    : defaultOpenLot(open, lineQty, { allowShortLots });
  if (!lot) {
    return {
      action: 'add',
      item,
      lotId: null,
      unitPrice: item.price,
      lotQty: item.stock,
      requiresLot: false,
    };
  }
  return {
    action: 'add',
    item,
    lotId: lot.lotId,
    unitPrice: lot.sellingPrice,
    lotQty: lot.qty,
    mrp: lot.mrp,
    requiresLot: true,
  };
}

export function cartLineKey(itemId, lotId) {
  return lotId ? `${itemId}::${lotId}` : itemId;
}

export function lotsDisagreeWithMeta(lots, metaPrice) {
  if (!lots?.length) return false;
  return availableLots(lots).some((lot) => !sameShelfMoney(lot.sellingPrice, metaPrice));
}

/**
 * Which open lots should the product editor offer to reprice after a catalog save.
 * Matching lots are left alone — do not call reprice when every open lot already
 * sells at the saved catalog price.
 *
 * - single: one open lot at another price
 * - shared: several lots that share one old price (preselect all, still confirm)
 * - mixed: several lots at different prices (checkboxes, nothing preselected)
 */
export function planShelfReprice(lots, catalogPrice) {
  const stale = availableLots(lots).filter((lot) => !sameShelfMoney(lot.sellingPrice, catalogPrice));
  if (stale.length === 0) {
    return { kind: 'none', lots: [], preselectedIds: [] };
  }
  if (stale.length === 1) {
    return { kind: 'single', lots: stale, preselectedIds: [stale[0].lotId] };
  }
  const prices = new Set(stale.map((lot) => Math.round(Number(lot.sellingPrice) * 100)));
  if (prices.size === 1) {
    return { kind: 'shared', lots: stale, preselectedIds: stale.map((lot) => lot.lotId) };
  }
  return { kind: 'mixed', lots: stale, preselectedIds: [] };
}

export function lotPriceRange(lots) {
  if (!lots?.length) return null;
  const prices = lots.map((l) => Number(l.sellingPrice));
  return { min: Math.min(...prices), max: Math.max(...prices) };
}
